import { motion, type Variants } from "motion/react";
import type { ReactNode } from "react";
import { cn } from "../lib/utils.js";

const EASE_OUT_EXPO: [number, number, number, number] = [0.16, 1, 0.3, 1];

const container: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.035 } },
};

const item: Variants = {
  hidden: { opacity: 0, y: 8 },
  show: { opacity: 1, y: 0, transition: { duration: 0.3, ease: EASE_OUT_EXPO } },
};

/** Wraps a list/table body so its rows reveal in a quick cascade on mount
 * instead of popping in as one flat block. */
export function StaggerContainer({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <motion.div variants={container} initial="hidden" animate="show" className={className}>
      {children}
    </motion.div>
  );
}

export function StaggerItem({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <motion.div variants={item} className={className}>
      {children}
    </motion.div>
  );
}

/**
 * Same cascade, for <tr> rows where a wrapping <div> would break table layout.
 * Carries TableRow's hairline itself: motion.tbody can't inherit TableBody's
 * `last:border-0`, and every table that hit that used to patch it row by row.
 */
export function StaggerRow({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <motion.tr
      variants={item}
      className={cn("border-b border-border transition-colors last:border-0 hover:bg-muted/70", className)}
    >
      {children}
    </motion.tr>
  );
}

/** tbody variant of StaggerContainer — a <div> there would be invalid HTML. */
export function StaggerBody({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <motion.tbody variants={container} initial="hidden" animate="show" className={className}>
      {children}
    </motion.tbody>
  );
}
